import { useEffect, useMemo, useState } from 'react';
import { PortfolioApi } from '../api/adminApi';

import {
  fallbackPortfolios,
  normalizeAlias,
  normalizeSlug,
  shapePortfolio,
  sortPortfolios
} from './portfolioHelpers';

const fallbackData = fallbackPortfolios;

let cachedPortfolios = null;
let cachedIsFallback = false;
let inflightRequest = null;

const fetchPortfolios = async () => {
  if (cachedPortfolios) {
    return { data: cachedPortfolios, isFallback: cachedIsFallback };
  }
  if (inflightRequest) {
    return inflightRequest;
  }
  inflightRequest = PortfolioApi.getAll()
    .then((data) => {
      if (Array.isArray(data) && data.length) {
        cachedPortfolios = sortPortfolios(data.map(shapePortfolio));
        cachedIsFallback = false;
      } else {
        cachedPortfolios = fallbackData;
        cachedIsFallback = true;
      }
      return { data: cachedPortfolios, isFallback: cachedIsFallback };
    })
    .catch(() => {
      cachedPortfolios = fallbackData;
      cachedIsFallback = true;
      return { data: cachedPortfolios, isFallback: cachedIsFallback };
    })
    .finally(() => {
      inflightRequest = null;
    });

  return inflightRequest;
};

export const usePortfolios = (serviceAlias) => {
  const [portfolios, setPortfolios] = useState(cachedPortfolios || fallbackData);
  const [isLoading, setIsLoading] = useState(!cachedPortfolios);
  const [error, setError] = useState('');
  const [isFallback, setIsFallback] = useState(cachedIsFallback || false);

  useEffect(() => {
    let isMounted = true;

    if (cachedPortfolios) {
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    fetchPortfolios()
      .then(({ data, isFallback: fallbackFlag }) => {
        if (isMounted) {
          setPortfolios(data);
          setIsFallback(fallbackFlag);
        }
      })
      .catch(() => {
        if (isMounted) {
          setError('Unable to load latest portfolio projects. Showing defaults.');
          setPortfolios(fallbackData);
          setIsFallback(true);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const normalizedAlias = normalizeAlias(serviceAlias);

  const filteredPortfolios = useMemo(() => {
    if (!normalizedAlias) {
      return portfolios;
    }
    return portfolios.filter((item) => item.serviceAlias === normalizedAlias);
  }, [normalizedAlias, portfolios]);

  return {
    portfolios: filteredPortfolios,
    isLoading,
    error,
    isFallback
  };
};

export const getCachedPortfolios = () => cachedPortfolios;

export const getCachedPortfolioBySlug = (slug) => {
  const normalized = normalizeSlug(slug);
  if (!normalized || !cachedPortfolios) {
    return null;
  }
  return cachedPortfolios.find((item) => item.slug === normalized) || null;
};
